import type {
  Attendee,
  CreateMeetingInput,
  MeetingStatus,
  MeetingType,
  Role,
  SessionUser,
  TranscriptSegment,
  TranscriptionProviderName,
  VocabularyCategory,
} from '@sl/shared';

export const API_BASE = '/api';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      ...(init.body && !(init.body instanceof FormData) && !(init.body instanceof Blob)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      message = body.message || body.error || message;
    } catch {
      /* keep the status message */
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

// ---------- shapes the UI consumes ----------

export interface MeetingRow {
  id: string;
  type: MeetingType;
  title: string;
  startedAt: string;
  durationSec: number | null;
  location: string | null;
  departmentId: string | null;
  clientRef: string | null;
  leadRef: string | null;
  status: MeetingStatus;
  failureDetail: string | null;
  language: 'AR' | 'EN' | 'BOTH';
  source: 'IN_PERSON' | 'UPLOAD' | 'ZOOM';
  attendees: Attendee[];
  department?: { id: string; name: string } | null;
  createdBy?: { id: string; name: string } | null;
  recording?: {
    id: string;
    uploadState: 'OPEN' | 'COMPLETE' | 'FAILED';
    durationSec: number | null;
    attempt?: number;
  } | null;
}

export interface TranscriptPayload {
  transcript: {
    id: string;
    provider: string;
    model: string;
    version: number;
    fullText: string;
    costUsd: number | null;
    createdAt: string;
  } | null;
  segments: TranscriptSegment[];
  audioUrl: string | null;
  audioUrlExpiresInSec?: number | null;
  status: MeetingStatus;
  failureDetail: string | null;
}

export interface VocabularyRow {
  id: string;
  term: string;
  category: VocabularyCategory;
  aliases: string[];
}

export interface DepartmentRow {
  id: string;
  name: string;
  headId: string | null;
}

export interface UserRow {
  id: string;
  name: string;
  email: string;
  role: Role;
  departmentId: string | null;
}

// ---------- endpoints ----------

export const authApi = {
  login: (email: string, password: string) =>
    api.post<{ user: SessionUser }>('/auth/login', { email, password }),
  logout: () => api.post<{ ok: true }>('/auth/logout'),
  me: () => api.get<{ user: SessionUser }>('/auth/me'),
};

export interface MeetingFilters {
  type?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  departmentId?: string;
  q?: string;
}

export const meetingsApi = {
  list: (filters: MeetingFilters) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) if (v) params.set(k, v);
    const qs = params.toString();
    return api.get<{ items: MeetingRow[]; total: number }>(`/meetings${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) => api.get<{ meeting: MeetingRow }>(`/meetings/${id}`),
  create: (input: CreateMeetingInput) => api.post<{ meeting: MeetingRow }>('/meetings', input),
  startRecording: (id: string, restart = false) =>
    api.post<{ recordingId: string; meetingId: string; attempt: number }>(
      `/meetings/${id}/recording`,
      { restart },
    ),
  completeRecording: (id: string, totalChunks: number, durationSec: number) =>
    api.post<{ ok: true; alreadyComplete?: boolean }>(`/meetings/${id}/recording/complete`, {
      totalChunks,
      durationSec,
    }),
  transcript: (id: string) => api.get<TranscriptPayload>(`/meetings/${id}/transcript`),
  updateSegment: (id: string, segId: string, body: { text?: string; speaker?: string }) =>
    api.patch<{ segment: TranscriptSegment }>(`/meetings/${id}/transcript/segments/${segId}`, body),
  mapSpeakers: (id: string, mappings: { from: string; to: string }[]) =>
    api.patch<{ updated: number; segments: TranscriptSegment[] }>(`/meetings/${id}/speakers`, {
      mappings,
    }),
  retranscribe: (id: string, provider?: TranscriptionProviderName) =>
    api.post<{ ok: true }>(`/meetings/${id}/retranscribe`, provider ? { provider } : {}),
  upload: async (id: string, file: File, onProgress?: (pct: number) => void) => {
    const form = new FormData();
    form.append('file', file);
    return new Promise<{ ok: true; objectKey: string }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE}/meetings/${id}/upload`);
      xhr.withCredentials = true;
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText) as { ok: true; objectKey: string });
        } else {
          reject(new ApiError(xhr.status, xhr.responseText || `HTTP ${xhr.status}`));
        }
      };
      xhr.onerror = () => reject(new ApiError(0, 'network error'));
      xhr.send(form);
    });
  },
};

export const vocabularyApi = {
  list: () => api.get<{ items: VocabularyRow[] }>('/vocabulary'),
  create: (body: { term: string; category: VocabularyCategory; aliases: string[] }) =>
    api.post<{ item: VocabularyRow }>('/vocabulary', body),
  update: (id: string, body: Partial<{ term: string; category: VocabularyCategory; aliases: string[] }>) =>
    api.patch<{ item: VocabularyRow }>(`/vocabulary/${id}`, body),
  remove: (id: string) => api.del<{ ok: true }>(`/vocabulary/${id}`),
};

export const directoryApi = {
  departments: () => api.get<{ items: DepartmentRow[] }>('/departments'),
  users: () => api.get<{ items: UserRow[] }>('/users'),
};
